<template>
  <transition name="dialog-fade" @after-enter="afterEnter" @after-leave="afterLeave">
    <div v-show="visible" class="el-dialog__wrapper" @click.self="handleWrapperClick">
      <div
        :key="key"
        ref="dialog"
        role="dialog"
        aria-modal="true"
        :aria-label="title || 'dialog'"
        :class="['el-dialog', { 'is-fullscreen': fullscreen, 'el-dialog--center': center }, customClass]"
        :style="style"
      >
        <div v-if="hasHeader" class="el-dialog__header">
          <slot name="title">
            <div class="el-dialog__title" :class="{ 'no-border': !title }">
              <span class="el-dialog__title-left">{{ title }}</span>
              <span class="el-dialog__title-right">
                <template v-if="hasFullscreen">
                  <cat-icon
                    v-if="!fullscreen"
                    name="common_icon_full"
                    prefix="iconfont"
                    @click="fullscreen = true"
                  ></cat-icon>
                  <cat-icon v-else name="common_icon_narrow" prefix="iconfont" @click="fullscreen = false"></cat-icon>
                </template>
                <cat-icon v-if="showClose" name="icon_cloose3" prefix="iconfont_zcpc" @click="handleClose"></cat-icon>
              </span>
            </div>
          </slot>
          <!-- <button v-if="showClose" type="button" class="el-dialog__headerbtn" aria-label="Close" @click="handleClose">
            <i class="el-dialog__close el-icon el-icon-close"></i>
          </button> -->
        </div>
        <div v-if="rendered" class="el-dialog__body" :style="dialogBodyStyle">
          <el-scrollbar>
            <slot></slot>
          </el-scrollbar>
        </div>
        <div v-if="hasFooter" class="el-dialog__footer-default">
          <el-button @click="handleClose">取 消</el-button>
          <el-button type="primary" :loading="submitLoading" @click="$emit('confirm')">{{ submitText }}</el-button>
        </div>
        <div v-else-if="$slots.footer" class="el-dialog__footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Migrating from 'element-ui/src/mixins/migrating';
import emitter from 'element-ui/src/mixins/emitter';
import Popup from './Popup';

export default {
  name: 'CatDialog',

  mixins: [Popup, emitter, Migrating],

  props: {
    title: {
      type: String,
      default: '',
    },

    modal: {
      type: Boolean,
      default: true,
    },

    modalAppendToBody: {
      type: Boolean,
      default: true,
    },

    appendToBody: {
      type: Boolean,
      default: true,
    },

    lockScroll: {
      type: Boolean,
      default: true,
    },

    closeOnClickModal: {
      type: Boolean,
      default: false,
    },

    closeOnPressEscape: {
      type: Boolean,
      default: true,
    },

    showClose: {
      type: Boolean,
      default: true,
    },

    width: {
      type: String,
      default: '50%',
    },

    customClass: {
      type: String,
      default: '',
    },

    top: {
      type: String,
      default: '15vh',
    },
    beforeClose: Function,
    center: {
      type: Boolean,
      default: false,
    },

    destroyOnClose: Boolean,

    hasFullscreen: {
      type: Boolean,
      default: true,
    },

    hasFooter: {
      type: Boolean,
      default: true,
    },
    hasHeader: {
      type: Boolean,
      default: true,
    },
    marginBottom: {
      type: String,
      default: '64px',
    },
    submitText: {
      type: String,
      default: '确定',
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['confirm'],

  data() {
    return {
      closed: false,
      key: 0,
      fullscreen: false,
    };
  },

  computed: {
    style() {
      let style = {};
      if (!this.fullscreen) {
        style.marginTop = this.top;
        if (this.width) {
          style.width = this.width;
        }
        // 非全屏时，高度为calc(100% - top - marginBottom) 保持在弹窗内滚动，弹窗外不显示滚动
        style.height = `calc(100% - ${this.top} - ${this.marginBottom})`;
      }
      return style;
    },
    dialogBodyStyle() {
      // 有footer或footer插槽时，padding-bottom为0，高度为calc(100% - 128px)
      const hasFooter = this.hasFooter || this.$slots.footer;
      return `
        padding-bottom: ${hasFooter ? '0' : '24px'};
        height: calc(100% - ${hasFooter ? '128px' : '64px'});
      `;
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.closed = false;
        this.$emit('open');
        this.$el.addEventListener('scroll', this.updatePopper);
        this.$nextTick(() => {
          this.$refs.dialog.scrollTop = 0;
        });
        if (this.appendToBody) {
          document.body.appendChild(this.$el);
        }
      } else {
        this.$el.removeEventListener('scroll', this.updatePopper);
        if (!this.closed) this.$emit('close');
        if (this.destroyOnClose) {
          this.$nextTick(() => {
            this.key++;
          });
        }
      }
    },
  },

  mounted() {
    if (this.visible) {
      this.rendered = true;
      this.open();
      if (this.appendToBody) {
        document.body.appendChild(this.$el);
      }
    }

    // 添加路由变化监听
    if (this.$router) {
      // 使用全局后置钩子来监听路由变化
      this.unwatchRoute = this.$router.afterEach((to, from) => {
        if (this.visible) {
          this.$emit('update:visible', false);
        }
      });
    }
  },

  destroyed() {
    // 移除路由监听
    if (this.unwatchRoute) {
      this.unwatchRoute();
    }

    // if appendToBody is true, remove DOM node after destroy
    if (this.appendToBody && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },

  methods: {
    getMigratingConfig() {
      return {
        props: {
          size: 'size is removed.',
        },
      };
    },
    handleWrapperClick() {
      if (!this.closeOnClickModal) return;
      this.handleClose();
    },
    handleClose() {
      if (typeof this.beforeClose === 'function') {
        this.beforeClose(this.hide);
      } else {
        this.hide();
      }
    },
    hide(cancel) {
      if (cancel !== false) {
        this.$emit('update:visible', false);
        this.$emit('close');
        this.closed = true;
      }
    },
    updatePopper() {
      this.broadcast('ElSelectDropdown', 'updatePopper');
      this.broadcast('ElDropdownMenu', 'updatePopper');
    },
    afterEnter() {
      this.$emit('opened');
    },
    afterLeave() {
      this.$emit('closed');
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'element-ui/packages/theme-chalk/src/dialog';

.el-dialog {
  border-radius: 4px;

  &.is-fullscreen {
    border-radius: 0;
  }

  &__title {
    width: 100%;
    background-color: #fff;
    border-bottom: 1px solid #e9e9e9;
    height: 64px;
    padding: 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 4px;

    &-left {
      opacity: 1;
      color: rgb(0 0 0 / 85%);
      font-size: 18px;
      font-weight: 500;
      font-style: normal;
      letter-spacing: 0;
      text-align: left;
      line-height: 26px;
    }

    &-right {
      .cat-icon {
        color: #595959;
        line-height: 64px;
        margin-left: 12px;
        font-size: 24px;
        cursor: pointer;
      }
    }

    &.no-border {
      border-bottom: none;
    }
  }

  &__footer-default {
    width: 100%;
    background-color: #fff;
    border-top: 1px solid #e9e9e9;
    height: 64px;
    padding: 0 24px;
    display: flex;
    justify-content: end;
    align-items: center;
  }
}

.el-dialog__wrapper {
  top: var(--nav-bar-height);
  left: var(--side-bar-width);
}

:deep {
  .el-scrollbar__wrap {
    overflow-x: hidden;
  }

  .el-dialog.is-fullscreen {
    overflow: hidden;
  }

  .el-dialog__body {
    padding: 0;
    overflow-y: auto;
    font-size: medium;

    .el-scrollbar {
      height: 100%;
      overflow-x: hidden;
    }

    .el-scrollbar__view {
      height: 100%;
    }
  }

  .el-dialog__header {
    padding: 0;
  }
}
</style>
